import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FiCloud, FiGlobe, FiTrendingUp, FiShield, FiBarChart, FiDatabase, FiCheckCircle, FiArrowRight, FiMonitor } from 'react-icons/fi';
import { trackPageView } from '../utils/analytics';
import { useSEO } from '../hooks/useSEO';
import './APM.css';

const CloudMonitoring = () => {
  useSEO({
    title: 'Cloud Monitoring Solution - Multi-Cloud Infrastructure Monitoring | LogCrest',
    description: 'Comprehensive cloud monitoring solution for AWS, GCP, Azure, and hybrid environments. Real-time infrastructure monitoring, log aggregation, and performance analytics.',
    keywords: 'cloud monitoring solution, cloud monitoring, multi-cloud monitoring, AWS monitoring, GCP monitoring, Azure monitoring, cloud infrastructure monitoring, cloud native observability, cloud logs and tracing'
  });

  useEffect(() => {
    trackPageView('/cloud-monitoring', {
      page_title: 'LogCrest Cloud Monitoring',
      page_category: 'cloud-monitoring'
    });
  }, []);

  const features = [
    {
      icon: <FiCloud />,
      title: 'Multi-Cloud Support',
      description: 'Monitor AWS, GCP, Azure, and hybrid cloud environments from a single platform.'
    },
    {
      icon: <FiGlobe />,
      title: 'Cloud-Native Observability',
      description: 'Built for cloud-native applications with Kubernetes and container monitoring.'
    },
    {
      icon: <FiTrendingUp />,
      title: 'Real-Time Cloud Monitoring',
      description: 'Real-time infrastructure monitoring with instant alerts and insights.'
    },
    {
      icon: <FiShield />,
      title: 'Cloud Security Monitoring',
      description: 'Monitor cloud security and compliance across all your cloud resources.'
    },
    {
      icon: <FiBarChart />,
      title: 'Cloud Performance Analytics',
      description: 'Comprehensive analytics for cloud infrastructure performance and costs.'
    },
    {
      icon: <FiDatabase />,
      title: 'Cloud Logs & Tracing',
      description: 'Centralized log aggregation and distributed tracing for cloud applications.'
    }
  ];

  const benefits = [
    'Multi-cloud monitoring for AWS, GCP, and Azure',
    'Cloud-native observability for Kubernetes',
    'Real-time cloud infrastructure monitoring',
    'Cloud logs and tracing in one platform',
    'Cloud security and compliance monitoring',
    'Cost-effective cloud monitoring solution'
  ];

  return (
    <div className="agent-sre-page">
      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <motion.div 
            className="hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1>Cloud Monitoring Solution</h1>
            <p className="hero-subtitle">
              Comprehensive cloud monitoring for AWS, GCP, Azure, and hybrid environments. Real-time infrastructure monitoring with cloud-native observability.
            </p>
            <div className="hero-actions">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link to="/pricing" className="btn btn-primary">
                  Start Free Trial
                  <FiArrowRight />
                </Link>
              </motion.div>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link to="/demo" className="btn btn-outline">
                  Request Demo
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>Multi-Cloud Infrastructure Monitoring</h2>
            <p>Cloud monitoring solution for modern cloud-native applications</p>
          </motion.div>

          <div className="features-grid">
            {features.map((feature, index) => (
              <motion.div 
                key={index}
                className="feature-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
              >
                <div className="feature-icon">
                  {feature.icon}
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits">
        <div className="container">
          <div className="benefits-content">
            <motion.div 
              className="benefits-text"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2>Why Choose Our Cloud Monitoring Solution?</h2>
              <p>
                Our cloud monitoring solution provides comprehensive visibility across all your cloud 
                infrastructure with real-time monitoring and cloud-native observability.
              </p>
              <ul className="benefits-list">
                {benefits.map((benefit, index) => (
                  <motion.li 
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <FiCheckCircle />
                    {benefit}
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            <motion.div 
              className="benefits-visual"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="dashboard-preview">
                <div className="dashboard-header">
                  <div className="dashboard-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                  <h4>Cloud Monitoring Dashboard</h4>
                </div>
                <div className="dashboard-content">
                  <div className="metric">
                    <FiCloud />
                    <span>Clouds Monitored: 3</span>
                  </div>
                  <div className="metric">
                    <FiTrendingUp />
                    <span>Resources: 2.5K</span>
                  </div>
                  <div className="metric">
                    <FiShield />
                    <span>Security Score: 98%</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta">
        <div className="container">
          <div className="cta-content">
            <h2>Ready for Cloud Monitoring?</h2>
            <p>Experience comprehensive cloud monitoring for AWS, GCP, Azure, and hybrid environments.</p>
            <div className="cta-actions">
              <Link to="/pricing" className="btn btn-primary">
                Start Free Trial
                <FiArrowRight />
              </Link>
              <Link to="/contact" className="btn btn-outline">
                Contact Sales
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CloudMonitoring;

